/**
 * PersonCard — UX §3.13. No avatar by default; Phosphor UserCircle +
 * name in serif + role in caption muted. Hairline divider between rows.
 */
import * as React from 'react';
import { UserCircle } from '@phosphor-icons/react';
import { cn } from '../../lib/cn';

// The shared/types `Person` entity uses `role: string | null` as a domain
// label ("Concierge", "Sommelier"). React's `HTMLAttributes.role` is the
// ARIA role and is `AriaRole | undefined`. We omit it from the base
// interface and add our domain `role` back as a domain field; ARIA role
// is rarely useful on the row (the inner button is the focusable element).
export interface PersonCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  name: string;
  role?: string | null;
  notes?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function PersonCard({
  name,
  role,
  notes,
  onEdit,
  onDelete,
  className,
  ...rest
}: PersonCardProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 py-4',
        'border-b border-ink-100 last:border-b-0',
        className,
      )}
      {...rest}
    >
      <UserCircle
        size={24}
        weight="regular"
        aria-hidden
        className="mt-0.5 text-ink-500 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg text-ink-900 leading-tight">
          {name}
        </div>
        {role ? (
          <div className="mt-0.5 text-xs text-ink-500 italic">{role}</div>
        ) : null}
        {notes ? (
          <p className="mt-2 text-sm text-ink-700 leading-relaxed">{notes}</p>
        ) : null}
      </div>
      {onEdit || onDelete ? (
        <div className="flex items-start gap-3 text-xs text-ink-500">
          {onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className="hover:text-ink-900 uppercase tracking-widest text-[0.65rem] focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Edit
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="hover:text-error-muted uppercase tracking-widest text-[0.65rem] focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Remove
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
